import { useCallback, useMemo } from 'react';
import { Switch } from '@cbhq/cds-web/controls';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline } from '@cbhq/cds-web/typography';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useUpdateGasPolicy } from ':cloud/queries/Base/useUpdateGasPolicy';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AttestationRequirement, GasPolicy } from ':cloud/types/ts_types';

type PaymasterRequirementsSectionProps = {
  gasPolicy: GasPolicy;
};

export function PaymasterRequirementsSection({ gasPolicy }: PaymasterRequirementsSectionProps) {
  const { activeOrg } = useGetUser();
  const { selectedProject } = useAppState();
  const updateGasPolicy = useUpdateGasPolicy({
    organizationId: activeOrg?.organizationId || '',
    projectId: selectedProject?.id || '',
    successMsg: 'Requirements updated',
  });

  const initialAttestationRequirements = useMemo(() => {
    return gasPolicy?.attestationRequirements?.map((requirement) => requirement.schemaId);
  }, [gasPolicy?.attestationRequirements]);

  const isCoinbaseVerifiedUserChecked = initialAttestationRequirements?.includes('cb_verified');

  const handleToggleCBVerifiedUserRequirement = useCallback(() => {
    if (!gasPolicy) {
      return;
    }
    const attestationRequirements: AttestationRequirement[] = [];
    if (!isCoinbaseVerifiedUserChecked) {
      attestationRequirements.push({ schemaId: 'cb_verified' });
    }
    const newGasPolicy = {
      ...gasPolicy,
      attestationRequirements,
    };
    logClick(EventName.paymaster_update_requirements);
    updateGasPolicy.mutate({ policy: newGasPolicy });
  }, [gasPolicy, isCoinbaseVerifiedUserChecked, updateGasPolicy]);

  return (
    <HStack gap={3} spacingTop={3} justifyContent="space-between" alignItems="center">
      <VStack>
        <TextHeadline as="p">Coinbase Verified User</TextHeadline>
        <TextBody as="p" color="foregroundMuted">
          Restrict your sponsorship to Coinbase-verified wallets
        </TextBody>
      </VStack>
      <Switch
        onChange={handleToggleCBVerifiedUserRequirement}
        checked={isCoinbaseVerifiedUserChecked}
      />
    </HStack>
  );
}
